<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<script lang="ts">
  import Panel from '@components/panel.svelte'
  import EntityLink from '@components/entity-link.svelte'
  import Image from '@components/image.svelte'
  import {
    getDisplay,
    getValue,
    shortenPosition,
    type GameraTeamReference,
    type NflStatKey,
    type StatModel,
  } from '@statmuse/core/gamera'
  import { playerStats, players } from './stores'

  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined
  export let domain: 'NFL' | 'CFB'
  export let renderImage = true

  type Selection = 'pass' | 'rush' | 'rec'

  const matchups = {
    pass: [
      {
        label: 'Pass Yds',
        statkey: 'Passing-Yards',
        compare: (a, b) =>
          getValue(a ?? {}, 'Passing-Yards') >
          getValue(b ?? {}, 'Passing-Yards'),
      },
      {
        label: 'Pass TD',
        statkey: 'Passing-Touchdowns',
        compare: (a, b) =>
          getValue(a ?? {}, 'Passing-Touchdowns') >
          getValue(b ?? {}, 'Passing-Touchdowns'),
      },
      {
        label: 'Int',
        statkey: 'Passing-Interceptions',
        compare: (a, b) =>
          getValue(a ?? {}, 'Passing-Interceptions') >
          getValue(b ?? {}, 'Passing-Interceptions'),
      },
    ],
    rush: [
      {
        label: 'Rush Yds',
        statkey: 'Rushing-Yards',
        compare: (a, b) =>
          getValue(a ?? {}, 'Rushing-Yards') >
          getValue(b ?? {}, 'Rushing-Yards'),
      },
      {
        label: 'Yds/Rush',
        statkey: 'Rushing-YardsPerAttempt',
        compare: (a, b) =>
          getValue(a ?? {}, 'Rushing-YardsPerAttempt') >
          getValue(b ?? {}, 'Rushing-YardsPerAttempt'),
      },
      {
        label: 'Rush TD',
        statkey: 'Rushing-Touchdowns',
        compare: (a, b) =>
          getValue(a ?? {}, 'Rushing-Touchdowns') >
          getValue(b ?? {}, 'Rushing-Touchdowns'),
      },
    ],
    rec: [
      {
        label: 'Rec',
        statkey: 'Receiving-Receptions',
        compare: (a, b) =>
          getValue(a ?? {}, 'Receiving-Receptions') >
          getValue(b ?? {}, 'Receiving-Receptions'),
      },
      {
        label: 'Rec Yds',
        statkey: 'Receiving-Yards',
        compare: (a, b) =>
          getValue(a ?? {}, 'Receiving-Yards') >
          getValue(b ?? {}, 'Receiving-Yards'),
      },
      {
        label: 'Rec TD',
        statkey: 'Receiving-Touchdowns',
        compare: (a, b) =>
          getValue(a ?? {}, 'Receiving-Touchdowns') >
          getValue(b ?? {}, 'Receiving-Touchdowns'),
      },
    ],
  } as Record<
    Selection,
    {
      label: string
      statkey: NflStatKey
      compare: (a?: StatModel<NflStatKey>, b?: StatModel<NflStatKey>) => boolean
    }[]
  >

  let selected: Selection = 'pass'

  $: selectedMatchup = matchups[selected]

  $: awayStats = [
    ...Object.entries($playerStats.away?.statsLookup ?? {}).filter((x) =>
      x[0].includes('p-'),
    ),
  ]

  $: homeStats = [
    ...Object.entries($playerStats.home?.statsLookup ?? {}).filter((x) =>
      x[0].includes('p-'),
    ),
  ]

  $: awayPasser = awayStats
    .filter((s) => getValue(s[1], 'Passing-Yards') > 0)
    .sort(
      (a, b) =>
        getValue(b[1], 'Passing-Yards') - getValue(a[1], 'Passing-Yards'),
    )[0] as [string, StatModel<NflStatKey>] | undefined

  $: homePasser = homeStats
    .filter((s) => getValue(s[1], 'Passing-Yards') > 0)
    .sort(
      (a, b) =>
        getValue(b[1], 'Passing-Yards') - getValue(a[1], 'Passing-Yards'),
    )[0] as [string, StatModel<NflStatKey>] | undefined

  $: awayRusher = awayStats
    .filter((s) => getValue(s[1], 'Rushing-Yards') > 0)
    .sort(
      (a, b) =>
        getValue(b[1], 'Rushing-Yards') - getValue(a[1], 'Rushing-Yards'),
    )[0] as [string, StatModel<NflStatKey>] | undefined

  $: homeRusher = homeStats
    .filter((s) => getValue(s[1], 'Rushing-Yards') > 0)
    .sort(
      (a, b) =>
        getValue(b[1], 'Rushing-Yards') - getValue(a[1], 'Rushing-Yards'),
    )[0] as [string, StatModel<NflStatKey>] | undefined

  $: awayReceiver = awayStats
    .filter((s) => getValue(s[1], 'Receiving-Yards') > 0)
    .sort(
      (a, b) =>
        getValue(b[1], 'Receiving-Yards') - getValue(a[1], 'Receiving-Yards'),
    )[0] as [string, StatModel<NflStatKey>] | undefined

  $: homeReceiver = homeStats
    .filter((s) => getValue(s[1], 'Receiving-Yards') > 0)
    .sort(
      (a, b) =>
        getValue(b[1], 'Receiving-Yards') - getValue(a[1], 'Receiving-Yards'),
    )[0] as [string, StatModel<NflStatKey>] | undefined

  $: selectedPlayerStats =
    selected === 'pass'
      ? [awayPasser, homePasser]
      : selected === 'rush'
      ? [awayRusher, homeRusher]
      : [awayReceiver, homeReceiver]

  $: awaySelectedPlayer =
    $players[selectedPlayerStats[0]?.[0].replace('p-', '').split(':')[2] ?? 0]
  $: homeSelectedPlayer =
    $players[selectedPlayerStats[1]?.[0].replace('p-', '').split(':')[2] ?? 0]
</script>

{#if awayStats.length > 0 || homeStats.length > 0}
  <Panel class="!p-0">
    <div class="flex justify-between items-center px-3 py-2">
      <h3 class="font-semibold">Matchup</h3>
      <div
        class="bg-gray-6 dark:bg-gray-4 text-gray-5 border border-gray-6 dark:border-gray-3 flex text-center rounded-full relative overflow-clip"
      >
        <span
          class="absolute left-0 h-full w-1/3 bg-gray-8 dark:bg-gray-7 rounded-full transition-transform ease-out"
          class:translate-x-0={selected === 'pass'}
          class:translate-x-full={selected === 'rush'}
          class:translate-x-24={selected === 'rec'}
        />
        <span
          class="py-1 relative cursor-pointer w-12"
          class:text-gray-2={selected === 'pass'}
          on:click={() => (selected = 'pass')}
        >
          Pass
        </span>
        <span
          class="py-1 relative cursor-pointer w-12"
          class:text-gray-2={selected === 'rush'}
          on:click={() => (selected = 'rush')}
        >
          Rush
        </span>
        <span
          class="py-1 relative cursor-pointer w-12"
          class:text-gray-2={selected === 'rec'}
          on:click={() => (selected = 'rec')}
        >
          Rec
        </span>
      </div>
    </div>
    {#if renderImage}
      <div
        class="flex justify-between px-3 border-b border-gray-6 dark:border-gray-4"
      >
        <EntityLink
          entity={awaySelectedPlayer && {
            type: 'player',
            id: awaySelectedPlayer.id,
            display: awaySelectedPlayer.longName,
            domain,
          }}
        >
          <Image
            src={awaySelectedPlayer?.media.bustOrSilhouette?.imageUrl ?? ''}
            alt={awaySelectedPlayer?.longName ?? ''}
            width={120}
            height={90}
            class="h-[90px] object-contain object-left"
          />
        </EntityLink>
        <EntityLink
          entity={homeSelectedPlayer && {
            type: 'player',
            id: homeSelectedPlayer.id,
            display: homeSelectedPlayer.longName,
            domain,
          }}
        >
          <Image
            src={homeSelectedPlayer?.media.bustOrSilhouette?.imageUrl ?? ''}
            alt={homeSelectedPlayer?.longName ?? ''}
            width={120}
            height={90}
            class="h-[90px] object-contain object-right"
          />
        </EntityLink>
      </div>
    {/if}
    <div class="divide-y divide-gray-6 dark:divide-gray-4 *:px-1 *:py-1">
      <div class="flex justify-between !px-3">
        <div>
          {#if awaySelectedPlayer}
            <EntityLink
              entity={awaySelectedPlayer && {
                type: 'player',
                id: awaySelectedPlayer.id,
                display: awaySelectedPlayer.longName,
                domain,
              }}
              class="font-semibold text-inherit"
            >
              {awaySelectedPlayer.longName}
            </EntityLink>

            <p class="text-[16px] text-gray-5">
              {awayTeam?.abbreviation}
              {shortenPosition(awaySelectedPlayer.position)} #{awaySelectedPlayer.uniformNumber}
            </p>
          {/if}
        </div>
        <div class="text-right">
          {#if homeSelectedPlayer}
            <EntityLink
              entity={homeSelectedPlayer && {
                type: 'player',
                id: homeSelectedPlayer.id,
                display: homeSelectedPlayer.longName,
                domain,
              }}
              class="font-semibold text-inherit"
            >
              {homeSelectedPlayer.longName}
            </EntityLink>

            <p class="text-[16px] text-gray-5">
              {homeTeam?.abbreviation}
              {shortenPosition(homeSelectedPlayer.position)} #{homeSelectedPlayer.uniformNumber}
            </p>
          {/if}
        </div>
      </div>
      {#each selectedMatchup as matchup (matchup.label)}
        <div class="flex justify-center relative">
          <p
            class="absolute top-1 left-1 px-2 rounded-2xl"
            style={matchup.compare(
              selectedPlayerStats[0]?.[1],
              selectedPlayerStats[1]?.[1],
            )
              ? `background:${awayTeam?.colors?.backgroundColor};color:${awayTeam?.colors?.foregroundColor};`
              : undefined}
          >
            {getDisplay(
              selectedPlayerStats[0] ? selectedPlayerStats[0][1] : {},
              matchup.statkey,
            )}
          </p>
          <p class="text-gray-5">{matchup.label}</p>
          <p
            class="absolute top-1 right-1 px-2 rounded-2xl"
            style={matchup.compare(
              selectedPlayerStats[1]?.[1],
              selectedPlayerStats[0]?.[1],
            )
              ? `background:${homeTeam?.colors?.backgroundColor};color:${homeTeam?.colors?.foregroundColor};`
              : undefined}
          >
            {getDisplay(
              selectedPlayerStats[1] ? selectedPlayerStats[1][1] : {},
              matchup.statkey,
            )}
          </p>
        </div>
      {/each}
    </div>
  </Panel>
{/if}
